import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import Lenis from "lenis";
import axios from "axios";

const AVAIL_API = `${process.env.REACT_APP_BACKEND_URL}/api/availability`;

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduced;
}

export function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    window.__lenis = lenis;
    return () => { cancelAnimationFrame(raf); lenis.destroy(); window.__lenis = null; };
  }, []);
  return null;
}

export function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function Reveal({ children, delay = 0, y = 26, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function Eyebrow({ children, dark = false, className = "", n = null }) {
  return (
    <div className={`flex items-center gap-4 ${className}`}>
      {n !== null && (
        <span className={`font-mono-tech text-[11px] tracking-[0.24em] ${dark ? "text-honey" : "text-brass"}`}>
          {String(n).padStart(2, "0")}
        </span>
      )}
      <span className={`font-mono-tech text-xs tracking-[0.28em] uppercase ${dark ? "text-honey" : "text-terracotta"}`}>
        {children}
      </span>
      <span className="eyebrow-rule" style={dark ? { background: "rgba(255,255,255,0.25)" } : {}} />
    </div>
  );
}

export function CountUp({ value, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(value);
  const numeric = parseFloat(String(value).replace(/[^0-9.]/g, ""));
  const suffix = String(value).replace(/[0-9.]/g, "");
  useEffect(() => {
    if (!inView || isNaN(numeric)) { setDisplay(value); return; }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setDisplay(value); return; }
    const dur = 1200;
    const t0 = performance.now();
    let raf = 0;
    const step = (t) => {
      const p = Math.min((t - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(`${Math.round(numeric * eased)}${suffix}`);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, numeric, suffix]);
  return <span ref={ref} className={className}>{display}</span>;
}

export function useAvailability() {
  const [avail, setAvail] = useState(null);
  useEffect(() => {
    axios.get(AVAIL_API).then((r) => setAvail(r.data)).catch(() => setAvail(null));
  }, []);
  return avail;
}

export function AvailabilityBadge({ dark = false, className = "" }) {
  const avail = useAvailability();
  if (!avail || !avail.open) return null;
  return (
    <span
      data-testid="availability-badge"
      className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 font-mono-tech text-[11px] tracking-wide uppercase ${
        dark ? "bg-ivory/15 text-honey" : "bg-sage/25 text-forest border border-sage/40"
      } ${className}`}
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full rounded-full bg-olive opacity-70 animate-ping" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-olive" />
      </span>
      {avail.message}
    </span>
  );
}

/**
 * errMsg — extract a renderable string from any axios/fetch error.
 * Defends against FastAPI validation `detail` arrays that would otherwise
 * crash React when passed to a toast as a child.
 */
export function errMsg(err, fallback = "Something went wrong.") {
  const d = err?.response?.data?.detail ?? err?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d)) {
    const parts = d
      .map((e) => (typeof e === "string" ? e : e?.msg || ""))
      .filter(Boolean);
    if (parts.length) return parts.join(", ");
  }
  if (typeof err?.message === "string" && err.message && !err.message.startsWith("Request failed")) {
    return err.message;
  }
  return fallback;
}
